import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { Text, IconButton, MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SuggestionStatus } from '@bingads-webui-campaign/media-repository';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { getCarouselStyles, getStyles } from './style';
import { useImageSuggestionCarouselState } from './state';


const CreativeEditorImageSuggestionCarousel = (props) => {
  const {
    isLoading,
    carousel,
    activity,
    setIsUploading,
    dispatchSuggestionState,
    assetLibraryConfig,
  } = props;

  const i18n = useContext(I18nContext);

  return (<ImageSuggestionCarousel
    isLoading={isLoading}
    images={carousel.images || []}
    isEmpty={!!carousel.isError}
    i18n={i18n}
    title={_TL_('Recommended images')}
    helpTopic="pop_BA_DisplayAdCreate_ImageRecs"
    selectable
    stylingOverrides={{
      getCarouselStyles,
      initialImageContainerLeftMargin: 0,
    }}
    onSelectImage={(imageUrl, index) => {
      setIsUploading(true);
      // Load the image first to reduce the time of the inner component to load the image
    const image = new Image();
    image.src = imageUrl;
    image.onload = () => {
      setIsUploading(false);
      dispatchSuggestionState({
        type: SuggestionStatus.SelectSuggestionComplete,
        selectedIndex: index,
        error: null,
      });
      assetLibraryConfig.onImageSelected({ url: imageUrl });

      activity.trace(`CarouselImageSelected: ${index};`);
    };
    image.onerror = () => {
      setIsUploading(false);
    };
    }}
    allowToggleSelectable={false}
    showViewAllInHeader
    showHeading={false}
  />);
};

CreativeEditorImageSuggestionCarousel.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  carousel: PropTypes.shape({}),
};

CreativeEditorImageSuggestionCarousel.defaultProps = {
  carousel: {},
};

const LoadingCell = () => (
  <div className={classNames('loading-cell')} />);

const LoadingCarousel = () => {
  const blockCount = 4;
  const loadingBlocks = Array.from({ length: blockCount }, (v, i) => i);
  const [imageContainerWidth, setImageContainerWidth] = useState(0);
  const imageContainerNode = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (imageContainerNode.current) {
        setImageContainerWidth(imageContainerNode.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const measuredRef = useCallback((node) => {
    if (node) {
      imageContainerNode.current = node;
      setImageContainerWidth(imageContainerNode.current.offsetWidth);
    }
  }, []);

  return (
    <div className={classNames('loading-carousel')}>
      <div className={classNames('loading-carousel-inner')}>
        <div
          className={classNames('loading-carousel-blocks-parent')}
          ref={measuredRef}
          style={imageContainerWidth > 0 ? { width: imageContainerWidth } : {}}
        >
          <div className={classNames('loading-carousel-blocks')}>
            {loadingBlocks.map(i => (<LoadingCell key={i} />))}
          </div>
        </div>
        <div className={classNames('loading-carousel-hint')} />
      </div >
    </div >
  );
};

const Title = () => {
  const i18n = useContext(I18nContext);
  return (
    <div className="item-title">
      <Text variant="smallPlus" >{i18n.getString(_TL_('Recommended images '))}</Text>
      <HelpPopupInline topic="pop_BA_DisplayAds_ImageRecs" />
    </div>
  );
};
const AddImageButton = (props) => {
  const {
    onChangeImage,
  } = props;
  return (<IconButton iconProps={{ iconName: 'Add' }} className={classNames('add-image-button')} onClick={onChangeImage} />);
};

export const CreativeEditorImageSuggestion = (props) => {
  const {
    url,
    hasErrorInUrl,
    oDataConfig,
    aid,
    cid,
    onChangeImage,
    assetLibraryConfig,
  } = props;

  const permissions = useContext(PermissionsContext);
  const instrumentation = useContext(InstrumentationContext);

  const createScenario = instrumentation.creativeEditorScenario.create();
  const activity = createScenario.imageSuggestionCarousel.create();
  const classes = useDefaultStyles(getStyles);

  const {
    isLoading,
    isUploading,
    setIsUploading,
    carousel,
    dispatchSuggestionState,
    setSuggestionErrorMessage,
    suggestionErrorMessage,
  } = useImageSuggestionCarouselState({
    aid,
    cid,
    url,
    instrumentation,
    activity,
    permissions,
    hasErrorInUrl,
    oDataConfig,
  });

  const i18n = useContext(I18nContext);

  return (
    <div className={classNames(classes.imageSuggestionCarousel, 'image-suggestion-carousel')}>
      <div className={classNames('title')}><Title /></div>
      <UiBlocker blocking={isUploading} loadingText={i18n.getString(_TL_('uploading'))}>
        <div className={classNames('add-image-banner')}>
          <AddImageButton onChangeImage={onChangeImage} />
          {isLoading ?
            (<LoadingCarousel />) :
            (<CreativeEditorImageSuggestionCarousel
              isLoading={isLoading}
              carousel={carousel}
              activity={activity}
              assetLibraryConfig={assetLibraryConfig}
              setIsUploading={setIsUploading}
              dispatchSuggestionState={dispatchSuggestionState}
            />)}
        </div>
        <div className={classNames('message-bar')}>
          {suggestionErrorMessage &&
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              onDismiss={() => setSuggestionErrorMessage(undefined)}
              dismissButtonAriaLabel="Close"
            >
              {suggestionErrorMessage}
            </MessageBar>}
        </div>
      </UiBlocker>
    </div>
  );
};

CreativeEditorImageSuggestion.propTypes = {
  oDataConfig: PropTypes.shape({}).isRequired,
  url: PropTypes.string,
  hasErrorInUrl: PropTypes.bool,
  aid: PropTypes.string,
  cid: PropTypes.string,
};

CreativeEditorImageSuggestion.defaultProps = {
  url: undefined,
  hasErrorInUrl: false, // TODO: validation from the outside
  aid: undefined,
  cid: undefined,
};
